// src/pages/Leaderboard.js
//
// Participant-facing leaderboard. Features:
//   - Points behind leader on each row
//   - Pick correctness rate (correct / decided picks)
//   - "Your row" highlight for the logged-in participant
//   - Current round indicator
//   - Last updated timestamp + 60-second auto-refresh
//   - Click row to navigate to BracketView for that participant

import React, { useEffect, useState, useCallback } from 'react';
import { getLeaderboard, getPools, getTournaments, getRounds } from '../services/api';

function Leaderboard({ user, onNavigate }) {
    const [tournaments,      setTournaments]      = useState([]);
    const [activeTournament, setActiveTournament] = useState(null);
    const [pools,            setPools]            = useState([]);
    const [activePool,       setActivePool]       = useState(null);
    const [standings,        setStandings]        = useState([]);
    const [rounds,           setRounds]           = useState([]);
    const [loading,          setLoading]          = useState(true);
    const [error,            setError]            = useState('');
    const [lastUpdated,      setLastUpdated]      = useState(null);
    const [showAll,          setShowAll]          = useState(false);

    useEffect(() => {
        getTournaments()
            .then(res => {
                setTournaments(res.data);
                if (res.data.length > 0) setActiveTournament(res.data[0]);
                else setLoading(false); // no tournaments → nothing to load
            })
            .catch(() => { setError('Failed to load tournaments'); setLoading(false); });
    }, []);

    useEffect(() => {
        if (!activeTournament) return;
        getPools(activeTournament.id)
            .then(res => {
                setPools(res.data);
                if (res.data.length > 0) setActivePool(res.data[0]);
                else { setActivePool(null); setLoading(false); } // no pools → nothing to load
            })
            .catch(() => { setError('Failed to load pools'); setLoading(false); });
        getRounds(activeTournament.id)
            .then(res => setRounds(res.data))
            .catch(() => setRounds([]));
    }, [activeTournament]);

    const fetchStandings = useCallback((isBackground = false) => {
        if (!activePool) return;
        getLeaderboard(activePool.id)
            .then(res => {
                setStandings(res.data);
                setLastUpdated(new Date());
                // Clear any previous error on success.
                setError('');
            })
            .catch(() => {
                // Background refresh failures are silent — don't alarm the
                // user or wipe standings just because a refresh hiccupped.
                // Only show the error banner on the initial/manual load.
                if (!isBackground) {
                    setError('Failed to load leaderboard');
                }
            })
            .finally(() => {
                if (!isBackground) setLoading(false);
            });
    }, [activePool]);

    useEffect(() => {
        if (!activePool) return;
        setLoading(true);
        fetchStandings(false);
    }, [activePool, fetchStandings]);

    // 60-second auto-refresh — background, silent on failure.
    useEffect(() => {
        const timer = setInterval(() => fetchStandings(true), 60000);
        return () => clearInterval(timer);
    }, [fetchStandings]);

    // Current round: lowest round_number where not all games are final.
    const currentRound = rounds
        .filter(r => r.game_count > 0 && parseInt(r.games_final || 0) < r.game_count)
        .sort((a, b) => a.round_number - b.round_number)[0] || null;

    // Leader's points for "behind" calculation.
    const leaderPoints = standings.length > 0 ? Number(standings[0].total_points) : 0;

    const VISIBLE_COUNT = 10;
    const myIndex = user
        ? standings.findIndex(e => Number(e.participant_id) === Number(user.id))
        : -1;
    const myEntryOutsideTop = myIndex >= VISIBLE_COUNT ? standings[myIndex] : null;
    const visibleStandings = showAll
        ? standings
        : standings.slice(0, VISIBLE_COUNT);

    return (
        <div className="max-w-3xl mx-auto px-4 py-8">
            <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
                <h2 className="text-2xl font-black text-white uppercase tracking-wide">
                    Leaderboard
                </h2>
                {lastUpdated && (
                    <div className="text-right">
                        <p className="text-xs text-gray-600">
                            Updated {lastUpdated.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                        </p>
                        <button
                            onClick={() => fetchStandings(true)}
                            className="text-xs text-gray-500 hover:text-[var(--org-accent,#fb923c)] transition-colors mt-0.5">
                            Refresh ↻
                        </button>
                    </div>
                )}
            </div>

            {/* Current round indicator */}
            {currentRound && (
                <div className="bg-[color-mix(in_srgb,var(--org-accent,#f97316)_20%,transparent)] border border-[color-mix(in_srgb,var(--org-accent,#f97316)_50%,transparent)] rounded-xl px-4 py-2.5 mb-5 flex items-center justify-between">
                    <p className="text-sm font-bold text-[var(--org-accent,#fb923c)]">{currentRound.round_name}</p>
                    <p className="text-xs text-gray-500">
                        {parseInt(currentRound.games_final || 0)} of {currentRound.game_count} games final
                    </p>
                </div>
            )}

            <div className="flex items-center gap-3 mb-6 flex-wrap">
                <select
                    value={activeTournament?.id || ''}
                    onChange={e => setActiveTournament(tournaments.find(t => t.id === parseInt(e.target.value)))}
                    className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[var(--org-accent,#f97316)]">
                    {tournaments.map(t => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                </select>
                <select
                    value={activePool?.id || ''}
                    onChange={e => setActivePool(pools.find(p => p.id === parseInt(e.target.value)))}
                    className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[var(--org-accent,#f97316)]">
                    {pools.length === 0 && <option value="">No pools</option>}
                    {pools.map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                </select>
            </div>

            {error && (
                <div className="bg-red-900/50 border border-red-500 text-red-300 px-4 py-3 rounded-lg mb-6 text-sm">
                    {error}
                </div>
            )}

            {loading ? (
                <div className="text-gray-400 text-center py-12">Loading standings...</div>
            ) : tournaments.length === 0 ? (
                <div className="text-gray-500 text-center py-12">
                    <p className="mb-2">No tournaments yet.</p>
                    <p className="text-sm">Set up your first tournament and pool from the Admin panel to get started.</p>
                </div>
            ) : standings.length === 0 ? (
                <div className="text-gray-500 text-center py-12">No standings yet for this pool.</div>
            ) : (
                <div className="space-y-3">
                    {(() => {
                        const rankCounts = standings.reduce((acc, e) => {
                            acc[e.rank] = (acc[e.rank] || 0) + 1;
                            return acc;
                        }, {});

                        const medalFor = (rank) => {
                            if (rankCounts[rank] !== 1) return null;
                            if (rank === 1) return '🥇';
                            if (rank === 2) return '🥈';
                            if (rank === 3) return '🥉';
                            return null;
                        };

                        return visibleStandings.map((entry) => {
                            const t1 = entry.tiebreaker_team1_score;
                            const t2 = entry.tiebreaker_team2_score;
                            const predictedTotal  = (t1 != null && t2 != null) ? t1 + t2 : null;
                            const tiebreakerActive = !!entry.tiebreaker_active;
                            const distance =
                                tiebreakerActive && predictedTotal != null && entry.championship_total != null
                                    ? Math.abs(predictedTotal - entry.championship_total)
                                    : null;

                            const medal           = medalFor(Number(entry.rank));
                            const isOutrightLeader = Number(entry.rank) === 1 && rankCounts[1] === 1;
                            const isMe            = user && Number(entry.participant_id) === Number(user.id);

                            // Points behind leader (only show when not the leader).
                            const pointsBehind = leaderPoints - Number(entry.total_points);

                            // Correctness rate: correct / (correct + wrong) decided picks.
                            const decided = Number(entry.correct_picks) + Number(entry.wrong_picks);
                            const correctRate = decided > 0
                                ? Math.round((Number(entry.correct_picks) / decided) * 100)
                                : null;

                            return (
                                <div key={entry.entry_id}
                                    onClick={() => onNavigate && onNavigate({
                                        tournamentId: activeTournament?.id,
                                        poolId:       activePool?.id,
                                        entryId:      entry.entry_id,
                                    })}
                                    className={
                                        'rounded-xl px-5 py-4 border flex items-center justify-between transition-colors ' +
                                        (isMe
                                            ? 'bg-[color-mix(in_srgb,var(--org-accent,#f97316)_15%,transparent)] border-[var(--org-accent,#f97316)] hover:bg-[color-mix(in_srgb,var(--org-accent,#f97316)_25%,transparent)]'
                                            : isOutrightLeader
                                                ? 'bg-gray-900 border-[var(--org-accent,#f97316)] hover:bg-gray-800'
                                                : 'bg-gray-900 border-gray-800 hover:bg-gray-800') +
                                        (onNavigate ? ' cursor-pointer' : '')
                                    }>
                                    <div className="flex items-center gap-4">
                                        <span className={medal ? 'text-2xl w-10 text-center' : 'text-lg font-bold w-10 text-center text-gray-500'}>
                                            {medal || `#${entry.rank}`}
                                        </span>
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <p className="font-bold text-white">{entry.participant}</p>
                                                {isMe && (
                                                    <span className="text-xs font-bold text-[var(--org-accent,#fb923c)] bg-[color-mix(in_srgb,var(--org-accent,#f97316)_30%,transparent)] px-1.5 py-0.5 rounded">
                                                        You
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-xs text-gray-400">
                                                {entry.correct_picks} correct &middot; {entry.wrong_picks} wrong &middot; {entry.pending_picks} pending
                                                {correctRate !== null && (
                                                    <> &middot; <span className="text-gray-500">{correctRate}% accuracy</span></>
                                                )}
                                            </p>
                                            {tiebreakerActive && (
                                                <p className="text-xs text-gray-500 mt-0.5">
                                                    {predictedTotal != null
                                                        ? <>Predicted {t1}–{t2} ({predictedTotal}) &middot; off by {distance}</>
                                                        : <>No tiebreaker entered</>}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-2xl font-black text-[var(--org-accent,#fb923c)]">
                                            {entry.total_points}
                                        </p>
                                        <p className="text-xs text-gray-500">pts</p>
                                        {pointsBehind > 0 && (
                                            <p className="text-xs text-gray-600 mt-0.5">
                                                −{pointsBehind} pts
                                            </p>
                                        )}
                                    </div>
                                </div>
                            );
                        });
                    })()}

                    {/* Pin the logged-in user's row when outside top 10 and collapsed */}
                    {!showAll && myEntryOutsideTop && (() => {
                        const entry     = myEntryOutsideTop;
                        const decided   = Number(entry.correct_picks) + Number(entry.wrong_picks);
                        const correctRate = decided > 0
                            ? Math.round((Number(entry.correct_picks) / decided) * 100)
                            : null;
                        const pointsBehind = leaderPoints - Number(entry.total_points);
                        return (
                            <div key="my-row-pin">
                                <div className="text-center text-xs text-gray-600 py-1">· · ·</div>
                                <div
                                    onClick={() => onNavigate && onNavigate({
                                        tournamentId: activeTournament?.id,
                                        poolId:       activePool?.id,
                                        entryId:      entry.entry_id,
                                    })}
                                    className={'rounded-xl px-5 py-4 border flex items-center justify-between transition-colors bg-[color-mix(in_srgb,var(--org-accent,#f97316)_15%,transparent)] border-[var(--org-accent,#f97316)] hover:bg-[color-mix(in_srgb,var(--org-accent,#f97316)_25%,transparent)]' + (onNavigate ? ' cursor-pointer' : '')}>
                                    <div className="flex items-center gap-4">
                                        <span className="text-lg font-bold w-10 text-center text-gray-500">#{entry.rank}</span>
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <p className="font-bold text-white">{entry.participant}</p>
                                                <span className="text-xs font-bold text-[var(--org-accent,#fb923c)] bg-[color-mix(in_srgb,var(--org-accent,#f97316)_30%,transparent)] px-1.5 py-0.5 rounded">You</span>
                                            </div>
                                            <p className="text-xs text-gray-400">
                                                {entry.correct_picks} correct &middot; {entry.wrong_picks} wrong &middot; {entry.pending_picks} pending
                                                {correctRate !== null && (
                                                    <> &middot; <span className="text-gray-500">{correctRate}% accuracy</span></>
                                                )}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-2xl font-black text-[var(--org-accent,#fb923c)]">{entry.total_points}</p>
                                        <p className="text-xs text-gray-500">pts</p>
                                        {pointsBehind > 0 && (
                                            <p className="text-xs text-gray-600 mt-0.5">−{pointsBehind} pts</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })()}

                    {/* Show all / Show less toggle — only when list exceeds visible count */}
                    {standings.length > VISIBLE_COUNT && (
                        <button
                            onClick={() => setShowAll(s => !s)}
                            className="w-full py-2.5 text-sm font-bold text-gray-500 hover:text-[var(--org-accent,#fb923c)] border border-gray-800 hover:border-[var(--org-accent,#f97316)] rounded-xl transition-colors mt-2">
                            {showAll
                                ? 'Show less ▲'
                                : `Show all ${standings.length} participants ▼`}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

export default Leaderboard;